import { loanApiService } from '../services/loanApi';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { trackingService } from '@/lib/tracking';
import { ApiError } from '@/types';
import type {
  ContractSignDeclarations,
  LoanContractResponse,
  LoanContractsHistory,
  LoanContractSummary,
} from '../types/loan';

export const LoanQueryKeys = {
  activeLoanSummary: ['active-loan-summary'],
  loanContractsHistory: ['loan-contracts-history'],
  currentContract: ['current-contract'],
  signCodeResendAt: ['contract-sign-code-resend-at'],
};

const RESEND_DELAY = 120;
const OTP_RATE_LIMITED = 'OtpRateLimited';

// The wait the backend asks for when it refused a contract code: the one
// sent moments ago still works.
export const getSignCodeRetryAfter = (error: unknown): number | null => {
  if (!(error instanceof ApiError) || error.status !== 429) return null;
  const details = error.details as
    | { code?: string; retryAfterSeconds?: number }
    | undefined;
  return details?.code === OTP_RATE_LIMITED
    ? Math.max(1, details.retryAfterSeconds ?? RESEND_DELAY)
    : null;
};
const ONE_HOUR = 3600 * 1000;

interface UseLoanReturn {
  downloadContract: (contractId: string) => Promise<string>;
  downloadContractError: Error | null;
  isDownloadingContract: boolean;
  // Resolves with the seconds until another code can be asked for.
  requestContractSignCode: (contractId: string) => Promise<number>;
  requestSignCodeError: Error | null;
  // The last code asked for was refused: one was sent moments before.
  isSignCodeRateLimited: boolean;
  signContractWithCode: (
    contractId: string,
    code: string,
    declarations: ContractSignDeclarations,
    orderId?: string
  ) => Promise<boolean>;
  signContractError: Error | null;
  getActiveLoanSummary: () => Promise<LoanContractSummary | null | undefined>;
  activeLoanSummary: LoanContractSummary | null | undefined;
  isLoading: boolean;
  resendDelay: number;
  getLoanContractsHistory: () => Promise<LoanContractsHistory[] | undefined>;
  loanContractsHistory: LoanContractsHistory[] | undefined;
  isLoadingLoanContractsHistory: boolean;
  getCurrentContract: () => Promise<LoanContractResponse | undefined>;
  currentContract: LoanContractResponse | undefined;
  isLoadingCurrentContract: boolean;
}

export function useLoan(
  fetchActiveLoanSummary: boolean = false
): UseLoanReturn {
  const queryClient = useQueryClient();
  const activeLoanSummaryQuery = useQuery({
    queryKey: [LoanQueryKeys.activeLoanSummary],
    queryFn: () => loanApiService.getActiveLoanSummary(),
    enabled: fetchActiveLoanSummary,
    staleTime: ONE_HOUR,
  });

  const getActiveLoanSummary = async () => {
    const result = await activeLoanSummaryQuery.refetch();
    return result.data;
  };

  const downloadContractMutation = useMutation({
    mutationFn: (contractId: string) =>
      loanApiService.downloadContract(contractId),
  });

  const requestContractSignCodeMutation = useMutation({
    mutationFn: (contractId: string) =>
      loanApiService.requestContractSignCode(contractId),
  });

  // When the next code can be asked for, across the screens sending one.
  const signCodeResendAtQuery = useQuery<number | null>({
    queryKey: LoanQueryKeys.signCodeResendAt,
    queryFn: () => null,
    initialData: null,
    enabled: false,
    staleTime: Infinity,
  });

  const waitBeforeResend = (seconds: number) => {
    queryClient.setQueryData(
      LoanQueryKeys.signCodeResendAt,
      Date.now() + seconds * 1000
    );
    return seconds;
  };

  const requestContractSignCode = async (contractId: string) => {
    try {
      await requestContractSignCodeMutation.mutateAsync(contractId);
      return waitBeforeResend(RESEND_DELAY);
    } catch (error) {
      const retryAfter = getSignCodeRetryAfter(error);
      if (retryAfter === null) throw error;
      return waitBeforeResend(retryAfter);
    }
  };

  const signCodeResendAt = signCodeResendAtQuery.data;
  const resendDelay = signCodeResendAt
    ? Math.max(0, Math.ceil((signCodeResendAt - Date.now()) / 1000))
    : RESEND_DELAY;
  const requestSignCodeError = requestContractSignCodeMutation.error;

  const currentContractQuery = useQuery({
    queryKey: [LoanQueryKeys.currentContract],
    queryFn: () => loanApiService.getCurrentContract(),
    staleTime: ONE_HOUR,
  });

  const getCurrentContract = async () => {
    const result = await currentContractQuery.refetch();
    return result.data;
  };

  const signContractWithCodeMutation = useMutation({
    mutationFn: ({
      contractId,
      code,
      declarations,
      orderId,
    }: {
      contractId: string;
      code: string;
      declarations: ContractSignDeclarations;
      orderId?: string;
    }) =>
      loanApiService.signContractWithCode(
        contractId,
        code,
        declarations,
        orderId
      ),
    onSuccess: (result, { contractId }) => {
      if (result) {
        // event_id = contractId so marketing platforms can deduplicate
        // repeated/cross-channel sends of the same signed contract.
        trackingService.trackEvent('contract_signed', { event_id: contractId });

        queryClient.invalidateQueries({
          queryKey: [LoanQueryKeys.activeLoanSummary],
        });

        queryClient.invalidateQueries({
          queryKey: [LoanQueryKeys.currentContract],
        });

        queryClient.invalidateQueries({
          queryKey: [LoanQueryKeys.loanContractsHistory],
        });
      }
    },
  });

  const loanContractsHistoryQuery = useQuery({
    queryKey: [LoanQueryKeys.loanContractsHistory],
    queryFn: () => loanApiService.getLoanContractsHistory(),
    staleTime: ONE_HOUR,
  });

  const getLoanContractsHistory = async () => {
    const result = await loanContractsHistoryQuery.refetch();
    return result.data;
  };

  return {
    downloadContract: downloadContractMutation.mutateAsync,
    downloadContractError: downloadContractMutation.error,
    isDownloadingContract: downloadContractMutation.isPending,
    requestContractSignCode,
    requestSignCodeError:
      getSignCodeRetryAfter(requestSignCodeError) === null
        ? requestSignCodeError
        : null,
    isSignCodeRateLimited: getSignCodeRetryAfter(requestSignCodeError) !== null,
    signContractWithCode: (
      contractId: string,
      code: string,
      declarations: ContractSignDeclarations,
      orderId?: string
    ) =>
      signContractWithCodeMutation.mutateAsync({
        contractId,
        code,
        declarations,
        orderId,
      }),
    signContractError: signContractWithCodeMutation.error,
    getActiveLoanSummary,
    activeLoanSummary: activeLoanSummaryQuery.data,
    isLoading:
      activeLoanSummaryQuery.isLoading ||
      downloadContractMutation.isPending ||
      requestContractSignCodeMutation.isPending ||
      signContractWithCodeMutation.isPending,
    resendDelay,
    getLoanContractsHistory,
    loanContractsHistory: loanContractsHistoryQuery.data,
    isLoadingLoanContractsHistory: loanContractsHistoryQuery.isLoading,
    getCurrentContract,
    currentContract: currentContractQuery.data,
    isLoadingCurrentContract: currentContractQuery.isLoading,
  };
}
